import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const ChatModal = ({ isOpen, onClose, selectedChat, onSendResponse, responderEmail }) => {
  const [response, setResponse] = useState('');
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [selectedChat]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Submit clicked, response:", response); // Debug log
    if (response.trim()) {
      onSendResponse(selectedChat.id, response);
      setResponse('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg h-[90vh] flex flex-col m-4">
        {/* Chat Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-xl font-semibold bg-gradient-to-r from-blue-500 to-green-500 text-transparent bg-clip-text">
            Chat with {selectedChat.userEmail}
          </h3>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Original Message */}
          <div className="flex flex-col space-y-4">
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%] shadow">
                <p className="text-sm text-gray-500 mb-1">{selectedChat.userEmail}</p>
                <p className="whitespace-pre-wrap break-words">{selectedChat.message}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {selectedChat.createdAt?.toDate?.() 
                    ? selectedChat.createdAt.toDate().toLocaleString() 
                    : new Date().toLocaleString()}
                </p>
              </div>
            </div>

            {/* Responses */}
            {selectedChat.responses?.map((resp, index) => (
              <div key={index} className="flex justify-end">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-2xl rounded-tr-none px-4 py-2 max-w-[80%] shadow">
                  <p className="text-sm opacity-75 mb-1">{resp.responderEmail}</p>
                  <p className="whitespace-pre-wrap break-words">{resp.response}</p>
                  <p className="text-xs opacity-75 mt-1">
                    {typeof resp.createdAt === 'string' 
                      ? new Date(resp.createdAt).toLocaleString()
                      : resp.createdAt?.toDate?.() 
                        ? resp.createdAt.toDate().toLocaleString() 
                        : new Date().toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div ref={messagesEndRef} />
        </div>

        {/* Response Input */}
        <div className="p-4 border-t">
          <form onSubmit={(e) => {
            e.preventDefault();
            onSendResponse(selectedChat.id, response);
            setResponse('');
          }} className="flex space-x-2">
            <input
              type="text"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              className="flex-1 px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Type your response..."
              autoFocus
            />
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-full p-2 w-10 h-10 flex items-center justify-center hover:opacity-90 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;